import {
  ItemsInCollectionDocument,
  LocationsGuidInput,
  SelectItem,
  useCreateManyItemsMutation,
  useItemTypesForItemCollectionTypeQuery,
  useLocationOptionsQuery,
} from '@kistill/client/data-access'
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogContentText,
  Grid,
  Portal,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import LoadingButton from '@mui/lab/LoadingButton'
import { useSnackbar } from 'notistack'
import { AutocompleteElement, SelectElement } from 'react-hook-form-mui'
import { KistillFormAutocompleteTitle } from '../Form/KistillFormAutocompleteTitle'
import { KistillFormContainer } from '../Form/KistillFormContainer'
import Draggable from 'react-draggable'
export interface CreateManyItemsProps {
  openCreateManyItemsPopup: boolean
  setOpenCreateManyItemsPopup: (deletePopup: boolean | undefined) => void
  itemCollectionGuid: string | undefined
}

export function CreateManyItemsPopup({
  openCreateManyItemsPopup,
  setOpenCreateManyItemsPopup,
  itemCollectionGuid,
}: CreateManyItemsProps) {
  const { enqueueSnackbar } = useSnackbar()
  const [itemCount, setItemCount] = useState<string>()
  const [itemTypeGuid, setItemTypeGuid] = useState<string>('')
  const [title, setTitle] = useState<SelectItem>()
  const [comment, setComment] = useState<string>()
  const [locationOptions, setLocationOptions] = useState<SelectItem[]>([])
  const [locationsGuid, setLocationsGuid] = useState<LocationsGuidInput>({
    locationsGuid: [],
  })

  const handleChange = () => {
    setOpenCreateManyItemsPopup(false)
  }
  // Get the ItemTypes for this ItemCollection
  const { data: itemTypesData } = useItemTypesForItemCollectionTypeQuery({
    variables: {
      itemCollectionGuid: itemCollectionGuid,
    },
    fetchPolicy: 'network-only',
  })
  const itemTypesOptions = itemTypesData?.itemTypesForItemCollectionType as
    | SelectItem[]
    | undefined

  //   useCreateManyItemsMutation
  const [createManyItems, { loading }] = useCreateManyItemsMutation()
  const createItems = async () => {
    await createManyItems({
      variables: {
        itemCount: itemCount ? Number(itemCount) : 0,
        itemCollectionGuid: itemCollectionGuid ? itemCollectionGuid : '',
        itemTypeGuid: itemTypeGuid ? itemTypeGuid : null,
        titleGuid: title?.id ? title.id : null,
        locations: locationsGuid ? locationsGuid : null,
        comment: comment ? comment : null,
      },
      refetchQueries: [
        {
          query: ItemsInCollectionDocument,
          variables: { itemCollectionGuid: itemCollectionGuid },
        },
      ],
      onCompleted: () => {
        enqueueSnackbar(`Eintak af fjölda ${itemCount} var skráð`, {
          variant: 'success',
        })
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any) => {
        enqueueSnackbar(`${error.message}`, {
          variant: 'error',
        })
      },
    })
    setOpenCreateManyItemsPopup(false)
  }

  // Get the LocationOptions for this ItemType
  const {
    data: locationOptionsData,
    refetch,
    loading: locationsLoading,
  } = useLocationOptionsQuery({
    variables: {
      itemTypeGuid: itemTypeGuid ? itemTypeGuid : '',
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    if (itemTypeGuid) {
      refetch()
    }
  }, [itemTypeGuid, refetch])

  useEffect(() => {
    if (locationOptionsData?.locationOptions) {
      setLocationOptions(
        locationOptionsData.locationOptions?.map((x) => {
          return { id: x.id, label: x.title } as unknown as SelectItem
        }),
      )
    }
  }, [locationOptionsData])

  const onLocationChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: SelectItem[],
    _reason: AutocompleteChangeReason,
    _details?: AutocompleteChangeDetails<unknown> | undefined,
  ) => {
    setLocationsGuid({ locationsGuid: value.map((x) => x.id) })
  }

  return (
    <Box style={{ position: 'relative' }}>
      <Portal>
        <Backdrop
          open={openCreateManyItemsPopup}
          style={{ zIndex: 1300, position: 'fixed' }}
        />
      </Portal>
      <Draggable>
        <Dialog
          open={openCreateManyItemsPopup}
          hideBackdrop={true}
          PaperProps={{
            sx: {
              width: '50%',
              maxWidth: 'none',
              m: 4,
              p: 4,
            },
          }}
        >
          <Typography component='h1' variant='h3' mb={2}>
            Framleiða mörg eintök
          </Typography>
          <KistillFormContainer FormProps={{ style: { width: '100%' } }}>
            <DialogContentText sx={{ mb: 1 }}>
              Hversu mörg eintök viltu framleiða?
            </DialogContentText>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <TextField
                  name='itemCount'
                  label='Fjöldi eintaka'
                  fullWidth
                  required
                  onChange={(e) => setItemCount(e.target.value)}
                  type='number'
                />
              </Grid>
              <Grid item xs={6}>
                <KistillFormAutocompleteTitle
                  name='titleGuid'
                  label='Titill'
                  multiple={false}
                  required={false}
                  onChange={(x) => x}
                  setSelectedTitle={(x) => setTitle(x)}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectElement
                  label='Tegund eintaks'
                  name='itemTypeGuid'
                  fullWidth
                  options={itemTypesOptions}
                  onChange={(e) => setItemTypeGuid(e)}
                />
              </Grid>
              <Grid item xs={6}>
                <AutocompleteElement
                  multiple
                  label='Staðsetning'
                  name='locations'
                  options={locationOptions}
                  loading={locationsLoading}
                  autocompleteProps={{
                    onChange: onLocationChange,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name='comment'
                  label='Athugasemd'
                  multiline
                  maxRows={5}
                  fullWidth
                  onChange={(e) => setComment(e.target.value)}
                  type='text'
                />
              </Grid>
            </Grid>

            <Grid item container justifyContent='flex-end' mt={4}>
              <Button
                variant='outlined'
                color='secondary'
                size='large'
                onClick={() => handleChange()}
                sx={{ mr: 4 }}
              >
                Hætta við
              </Button>
              <LoadingButton
                variant='contained'
                size='large'
                loading={loading}
                onClick={() => createItems()}
                disabled={!itemCount}
              >
                Skrá
              </LoadingButton>
            </Grid>
          </KistillFormContainer>
        </Dialog>
      </Draggable>
    </Box>
  )
}
